import { Component, Injector, Input, OnInit, ViewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ModalDirective } from 'ngx-bootstrap/modal';
import { BaseCrudModalComponent } from 'projects/ProjetoBaseAngular/app/base-crud-modal.component';
import { TypesService } from 'projects/admin/src/domain/services';
import { MetaMsgTemplateComponentModel, MetaMsgTemplateParametroModel } from '../../../../domain/models';
import { MetaMsgTemplateComponentType } from '../../../../domain/types';
import { MetaMsgTemplateComponentService } from '../../../../domain/services/meta-msg-template-component.service';
import { MetaMsgTemplateParametroService } from '../../../../domain/services/meta-msg-template-parametro.service';
import Swal from 'sweetalert2';
import { MetaMsgTemplateComponentParametroModalComponent } from './parametro-modal/parametro-modal.component';
import { MetaMsgTemplateParametroNomeMaxLength, MetaMsgTemplateParametroOrigemGrupos } from 'projects/admin/src/domain/consts';

@Component({
  selector: 'app-meta-msg-template-component-modal',
  templateUrl: './msg-template-component-modal.component.html'
})
export class MetaMsgTemplateComponentModalComponent extends BaseCrudModalComponent<MetaMsgTemplateComponentModel> implements OnInit {
  @ViewChild('modal') modal: ModalDirective;
  @ViewChild('parametroModal') parametroModal: MetaMsgTemplateComponentParametroModalComponent;
  @Input('metaMsgTemplateId') metaMsgTemplateId: string;
  @Input() templateConfirmacaoPresenca = false;

  constructor(
    injector: Injector,
    readonly service: MetaMsgTemplateComponentService,
    readonly parametroService: MetaMsgTemplateParametroService,
    readonly typesService: TypesService
  ) {
    super(injector, service);
  }

  parametros: MetaMsgTemplateParametroModel[] = [];
  readonly parametroNomeMaxLength = MetaMsgTemplateParametroNomeMaxLength;
  private originalParametroIds: string[] = [];
  private deletedParametroIds: string[] = [];

  ngOnInit() {
    super.ngOnInit();
  }

  protected initializeForm(model: MetaMsgTemplateComponentModel) {
    this.parametros = (model.parametros || [])
      .map(parametro => this.cloneParametro(parametro))
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0));
    this.originalParametroIds = this.parametros
      .filter(parametro => !!parametro.id)
      .map(parametro => parametro.id);
    this.deletedParametroIds = [];

    this.form = this.formBuilder.group({
      id: [model.id],
      dataInclusao: [model.dataInclusao, Validators.required],
      dataAlteracao: [model.dataAlteracao],
      registroAtivo: [model.registroAtivo],
      metaMsgTemplateId: [model.metaMsgTemplateId, Validators.required],
      tipo: [model.tipo, Validators.required],
      format: [model.format],
      text: [model.text, Validators.maxLength(1000)],
      exampleJson: [model.exampleJson, Validators.maxLength(2000)],
      order: [model.order]
    });
  }

  protected newModel(): MetaMsgTemplateComponentModel {
    const model = new MetaMsgTemplateComponentModel();
    model.id = this.commonService.newGuid();
    model.dataInclusao = new Date();
    model.registroAtivo = true;
    model.metaMsgTemplateId = this.metaMsgTemplateId;
    model.tipo = MetaMsgTemplateComponentType.Body;
    model.format = null;
    model.text = "";
    model.exampleJson = "";
    model.order = 1;
    model.parametros = [];

    return model;
  }

  componentPermiteParametros(): boolean {
    const tipo = this.form?.value?.tipo;
    return tipo === MetaMsgTemplateComponentType.Header || tipo === MetaMsgTemplateComponentType.Body;
  }

  addParametro() {
    const parametro = this.newParametro();
    parametro.ordem = this.getNextParametroOrder();
    this.parametros.push(parametro);
  }

  removeParametro(parametro: MetaMsgTemplateParametroModel) {
    if (parametro.id && this.originalParametroIds.includes(parametro.id)) {
      this.deletedParametroIds.push(parametro.id);
    }

    this.parametros = this.parametros.filter(x => x !== parametro);
    this.reordenarParametros();
  }

  gerarParametros() {
    if (!this.componentPermiteParametros()) {
      this.commonService.mensagem('', 'Somente componentes de cabeçalho e corpo aceitam parâmetros.', 'info');
      return;
    }

    const text = this.form?.value?.text || '';
    const regex = /\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g;
    const nomes: string[] = [];
    let match: RegExpExecArray | null;

    while ((match = regex.exec(text)) !== null) {
      const nome = match[1];
      if (!nomes.includes(nome)) {
        nomes.push(nome);
      }
    }

    const parametrosAtuais = this.parametros.reduce((result, parametro) => {
      result[parametro.nome] = parametro;
      return result;
    }, {} as { [nome: string]: MetaMsgTemplateParametroModel });

    const parametrosGerados = nomes.map((nome, index) => {
      const parametro = parametrosAtuais[nome] || this.newParametro();
      parametro.nome = nome;
      parametro.ordem = index + 1;
      parametro.exemplo = parametro.exemplo || nome;
      return parametro;
    });

    this.parametros
      .filter(parametro => parametro.id && this.originalParametroIds.includes(parametro.id))
      .filter(parametro => !nomes.includes(parametro.nome))
      .forEach(parametro => this.deletedParametroIds.push(parametro.id));

    this.parametros = parametrosGerados;
    this.deletedParametroIds = [...new Set(this.deletedParametroIds)];
  }

  editParametro(parametro: MetaMsgTemplateParametroModel) {
    this.parametroModal.show(parametro);
  }

  onParametroSave(parametro: MetaMsgTemplateParametroModel) {
    const index = this.parametros.findIndex(x => x.id === parametro.id);
    if (index >= 0) {
      this.parametros[index] = parametro;
    }
  }

  gerarExampleJson() {
    if (!this.componentPermiteParametros()) {
      this.commonService.mensagem('', 'Somente componentes de cabeçalho e corpo possuem exemplo JSON de parâmetros.', 'info');
      return;
    }

    if (this.parametros.length === 0) {
      this.form.patchValue({ exampleJson: '' });
      this.commonService.mensagem('', 'Não existem parâmetros para gerar o exemplo JSON.', 'info');
      return;
    }

    this.form.patchValue({ exampleJson: this.buildExampleJson() });
  }

  async save(autoSave: boolean = false) {
    if (!await this.validateToSave(autoSave)) {
      return;
    }

    if (!this.validateParametros()) {
      return;
    }

    this.isBusy = true;
    if (this.componentPermiteParametros() && this.parametros.length > 0) {
      this.form.patchValue({ exampleJson: this.buildExampleJson() });
    }

    let model: MetaMsgTemplateComponentModel = Object.assign({}, this.form.value);
    model = await this.beforeSave(model);

    try {
      model = this.newItem
        ? await this.service.create(model).toPromise()
        : await this.service.edit(model).toPromise();

      this.form.patchValue({ id: model.id });
      await this.saveParametros(model.id);
      this.afterSave(model);

      if (autoSave) {
        this.newItem = false;
        this.oldModel = null;
      } else if (this.multiCreate) {
        this.lastModel = model;
        this.form = null;
        let newModel = this.newModel();
        newModel.id = newModel.id || this.commonService.newGuid();
        newModel.dataInclusao = new Date();
        newModel = await this.beforeShowCreate(newModel);
        this.initializeForm(newModel);
        this.onShown();
      } else {
        this.close();
      }

      if (!autoSave) {
        this.modalSave.emit(model.id);
      }

      this.isBusy = false;
    } catch (errors) {
      this.verifyErrors(errors);
      this.isBusy = false;
      const message = Array.isArray(errors) ? errors.join('/n') : (this.errors || []).join('/n');
      Swal.fire(
        'Oops! Algo deu errado',
        message,
        'error');
    }
  }

  private async saveParametros(metaMsgTemplateComponentId: string) {
    for (const id of this.deletedParametroIds) {
      await this.parametroService.delete(id).toPromise();
    }

    if (!this.componentPermiteParametros()) {
      const idsToDelete = this.originalParametroIds.filter(id => !this.deletedParametroIds.includes(id));
      for (const id of idsToDelete) {
        await this.parametroService.delete(id).toPromise();
      }

      this.parametros = [];
      this.deletedParametroIds = [];
      this.originalParametroIds = [];
      return;
    }

    for (const parametro of this.parametros) {
      parametro.metaMsgTemplateComponentId = metaMsgTemplateComponentId;
      parametro.dataInclusao = parametro.dataInclusao || new Date();
      parametro.registroAtivo = parametro.registroAtivo !== false;
      parametro.ordem = parametro.ordem || this.getNextParametroOrder();

      if (parametro.id && this.originalParametroIds.includes(parametro.id)) {
        await this.parametroService.edit(parametro).toPromise();
      } else {
        parametro.id = parametro.id || this.commonService.newGuid();
        await this.parametroService.create(parametro).toPromise();
      }
    }

    this.originalParametroIds = this.parametros.map(parametro => parametro.id);
    this.deletedParametroIds = [];
  }

  private validateParametros(): boolean {
    this.errors = this.errors || [];

    if (!this.componentPermiteParametros()) {
      return true;
    }

    const nomes = new Set<string>();
    const ordens = new Set<number>();

    for (const parametro of this.parametros) {
      if (!parametro.nome) {
        this.errors.push('Informe o nome de todos os parâmetros.');
      } else if (parametro.nome.length > this.parametroNomeMaxLength) {
        this.errors.push(`Parâmetro '${parametro.nome}': o nome deve ter no máximo ${this.parametroNomeMaxLength} caracteres.`);
      } else if (!/^[a-zA-Z0-9_]+$/.test(parametro.nome)) {
        this.errors.push(`Parâmetro '${parametro.nome}': use apenas letras, números e underscore.`);
      } else if (nomes.has(parametro.nome)) {
        this.errors.push(`Parâmetro '${parametro.nome}' está duplicado.`);
      }

      if (!parametro.ordem || parametro.ordem < 1) {
        this.errors.push(`Parâmetro '${parametro.nome || ''}': informe uma ordem maior que zero.`);
      } else if (ordens.has(parametro.ordem)) {
        this.errors.push(`Ordem '${parametro.ordem}' está duplicada nos parâmetros.`);
      }

      if (!parametro.exemplo) {
        this.errors.push(`Parâmetro '${parametro.nome || ''}': informe um exemplo.`);
      }

      if (this.templateConfirmacaoPresenca && !parametro.origem) {
        this.errors.push(`Parâmetro '${parametro.nome || ''}': informe a origem para uso na confirmação de presença.`);
      } else if (this.templateConfirmacaoPresenca && !this.isOrigemSuportada(parametro.origem)) {
        this.errors.push(`Parâmetro '${parametro.nome || ''}': a origem '${parametro.origem}' não é suportada para confirmação de presença.`);
      }

      nomes.add(parametro.nome);
      ordens.add(parametro.ordem);
    }

    return this.errors.length === 0;
  }

  isOrigemSuportada(origem: string): boolean {
    return MetaMsgTemplateParametroOrigemGrupos.some(grupo =>
      grupo.opcoes.some(opcao => opcao.valor === origem));
  }

  private newParametro(): MetaMsgTemplateParametroModel {
    const parametro = new MetaMsgTemplateParametroModel();
    parametro.id = this.commonService.newGuid();
    parametro.dataInclusao = new Date();
    parametro.registroAtivo = true;
    parametro.metaMsgTemplateComponentId = this.form?.value?.id;
    parametro.nome = '';
    parametro.ordem = 1;
    parametro.exemplo = '';
    parametro.origem = '';
    parametro.descricao = '';
    return parametro;
  }

  private cloneParametro(parametro: MetaMsgTemplateParametroModel): MetaMsgTemplateParametroModel {
    return Object.assign(new MetaMsgTemplateParametroModel(), parametro);
  }

  private buildExampleJson(): string {
    const prefix = this.form.value.tipo === MetaMsgTemplateComponentType.Header ? 'header' : 'body';
    const parametros = [...this.parametros]
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
      .filter(parametro => parametro.nome);

    const example = {
      [`${prefix}_text_named_params`]: parametros.map(parametro => ({
        param_name: parametro.nome,
        example: parametro.exemplo || parametro.nome
      }))
    };

    return JSON.stringify(example, null, 2);
  }

  private getNextParametroOrder(): number {
    const ordens = this.parametros.map(parametro => parametro.ordem || 0);
    return ordens.length ? Math.max(...ordens) + 1 : 1;
  }

  private reordenarParametros() {
    this.parametros
      .sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
      .forEach((parametro, index) => parametro.ordem = index + 1);
  }
}
